import { FC, useEffect, useMemo, useState } from 'react'
import { FaArrowLeft as ArrowIcon, FaChevronDown as ChevronIcon, FaMagnifyingGlass as GlassIcon } from 'react-icons/fa6'
import { PiSortAscendingBold as SortIcon } from 'react-icons/pi'

import { Button } from '@ui/components/core'
import { ServiceGroup, TracesGroupBy, TraceViewStates } from '@ui/interfaces'
import clsx from 'clsx'
import { debounce } from 'lodash'

type SortDropdown = {
  handleSort: (column: string, direction: string) => void
  groupBy: TracesGroupBy
  orderBy?: { column: string; direction: string }
}

const SortDropdown: FC<SortDropdown> = ({ groupBy, handleSort, orderBy }) => {
  const serviceSortOptions = [
    { label: 'Total size ascending', column: 'total_size', direction: 'asc' },
    { label: 'Total size descending', column: 'total_size', direction: 'desc' },
    { label: 'Count ascending', column: 'count', direction: 'asc' },
    { label: 'Count descending', column: 'count', direction: 'desc' },
  ]

  const attributeSortOptions = [
    { label: 'Count ascending', column: 'count', direction: 'asc' },
    { label: 'Count descending', column: 'count', direction: 'desc' },
  ]

  const sortOptions = groupBy === TracesGroupBy.SERVICE ? serviceSortOptions : attributeSortOptions

  return (
    <div className="absolute right-0 top-9 z-20 w-60 space-y-1 rounded-md border border-gray-200 bg-white shadow-lg">
      {sortOptions.map((option) => (
        <div
          key={option.label}
          className={clsx('flex cursor-pointer flex-row items-center p-2.5 hover:bg-gray-100', {
            'bg-gray-100': orderBy?.column === option.column && orderBy?.direction === option.direction,
          })}
          onClick={() => handleSort(option.column, option.direction)}>
          <SortIcon
            className="mr-2 h-5 w-5 text-gray-500"
            style={{ transform: option.direction === 'asc' ? 'rotate(180deg)' : 'none' }}
          />
          <p className="text-sm font-medium">{option.label}</p>
        </div>
      ))}
    </div>
  )
}

interface TraceGroupTableHeaderProps {
  traceViewState: TraceViewStates
  setTraceViewState: (state: TraceViewStates) => void
  groupBy: TracesGroupBy
  setGroupBy: (value: TracesGroupBy) => void
  setWhereClause: (value: string) => void
  selectedGroup?: ServiceGroup | { name: string; value: string } | null
  orderBy?: { column: string; direction: string }
  setOrderBy?: (value: { column: string; direction: string }) => void
  loading?: boolean
  count?: number
}

export const TraceGroupTableHeader = ({
  traceViewState,
  setTraceViewState,
  groupBy,
  setGroupBy,
  setWhereClause,
  selectedGroup,
  orderBy,
  setOrderBy,
  loading = false,
  count,
}: TraceGroupTableHeaderProps) => {
  const [localWhereClause, setLocalWhereClause] = useState<string>('')
  const [showSortDropdown, setShowSortDropdown] = useState(false)

  const handleSort = (column: string, direction: string) => {
    setOrderBy?.({ column, direction })
    setShowSortDropdown(false)
  }

  const debouncedUpdate = useMemo(
    () =>
      debounce(
        (value: string) => {
          setWhereClause(value)
        },
        750,
        { trailing: true }
      ),
    [setWhereClause]
  )

  const setInput = (value: string) => {
    setLocalWhereClause(value)
    debouncedUpdate(value)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('#filter-dropdown')) {
        setShowSortDropdown(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!loading) {
      document.getElementById('search-trace-groups')?.focus()
    }
  }, [loading])

  const pluralCountText = count === 1 ? '' : 's'

  return (
    <div className="flex h-9 w-full">
      {traceViewState === TraceViewStates.GROUP ? (
        <div className="flex w-full flex-row items-center justify-between">
          <div className="flex flex-row items-center">
            <h2 className="text-md font-medium">View traces by</h2>
            <div className="ml-2 flex flex-row space-x-2 p-2.5">
              <Button
                variant={groupBy === TracesGroupBy.SERVICE ? 'primary' : 'secondary'}
                onClick={() => setGroupBy(TracesGroupBy.SERVICE)}>
                Services
              </Button>
              <Button
                variant={groupBy === TracesGroupBy.ATTRIBUTE ? 'primary' : 'secondary'}
                onClick={() => setGroupBy(TracesGroupBy.ATTRIBUTE)}>
                Attributes
              </Button>
              <div className="relative">
                <GlassIcon className="absolute left-2 top-2.5 text-gray-500" />
                <input
                  id="search-trace-groups"
                  type="text"
                  placeholder="Search"
                  className="h-full min-w-[20rem] rounded-md border border-gray-200 py-1 pl-8 pr-2 focus:ring-primary-500"
                  value={localWhereClause}
                  disabled={loading}
                  onChange={(e) => setInput(e.target.value)}
                />
              </div>
              <div className="relative h-full" id="filter-dropdown">
                <Button variant="secondary" onClick={() => setShowSortDropdown(!showSortDropdown)}>
                  Sort <ChevronIcon className="ml-1" />
                </Button>
                {showSortDropdown && <SortDropdown handleSort={handleSort} groupBy={groupBy} orderBy={orderBy} />}
              </div>
            </div>
          </div>

          <p className="text-sm font-medium text-gray-600">
            {localWhereClause.length > 0
              ? `Showing ${count} result${pluralCountText} for "${localWhereClause}"`
              : `Showing ${count} ${
                  groupBy === TracesGroupBy.ATTRIBUTE ? `attribute${pluralCountText}` : `service${pluralCountText}`
                }`}
          </p>
        </div>
      ) : (
        <div className="mr-2 flex h-9 w-full flex-row items-center justify-between">
          <div className="my-2 flex h-full flex-row items-center space-x-2.5">
            <Button variant="secondary" onClick={() => setTraceViewState(TraceViewStates.GROUP)}>
              <ArrowIcon className="mr-1" />
              Back
            </Button>
            <h2 className="flex h-full items-center rounded-md border border-gray-200 bg-white p-2.5 text-sm font-medium text-gray-600">
              <span className="font-medium text-gray-900">
                {groupBy === TracesGroupBy.ATTRIBUTE ? 'Attribute: ' : 'Service: '}
              </span>
              {groupBy === TracesGroupBy.ATTRIBUTE ? selectedGroup?.name : (selectedGroup as ServiceGroup)?.serviceName}
            </h2>
            <h2 className="flex h-full items-center rounded-md border border-gray-200 bg-white p-2.5 text-sm font-medium text-gray-600">
              <span className="font-medium text-gray-900">
                {groupBy === TracesGroupBy.ATTRIBUTE ? 'Value: ' : 'Name: '}
              </span>
              {groupBy === TracesGroupBy.ATTRIBUTE
                ? (selectedGroup as { name: string; value: string })?.value
                : selectedGroup?.name}
            </h2>
          </div>

          <p className="text-sm font-medium text-gray-600">{`Showing ${count} span${pluralCountText}`}</p>
        </div>
      )}
    </div>
  )
}
